import React from 'react';
import { Box, Button, LinearProgress, Skeleton, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon, ErrorOutline as ErrorIcon } from '@mui/icons-material';
import { UI } from './chartTheme';

interface ChartCardProps {
  eyebrow: string;
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  controls?: React.ReactNode;
  /** True while data is (re)loading. */
  loading: boolean;
  /** Load failure to show in place of the empty state. */
  error?: string | null;
  onRetry?: () => void;
  /** Whether there is anything to plot; false shows the empty state. */
  hasData: boolean;
  empty: { icon: React.ReactNode; title: string; body: string };
  onUploadClick?: () => void;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const ChartCard: React.FC<ChartCardProps> = ({
  eyebrow,
  title,
  subtitle,
  actions,
  controls,
  loading,
  error,
  onRetry,
  hasData,
  empty,
  onUploadClick,
  footer,
  children,
}) => (
  <Box
    component="section"
    aria-label={title}
    sx={{
      position: 'relative',
      background: 'linear-gradient(180deg, rgba(18, 24, 21, 0.85) 0%, rgba(10, 14, 12, 0.95) 100%)',
      border: `1px solid ${UI.border}`,
      borderRadius: '12px',
      overflow: 'hidden',
    }}
  >
    {/* Refetch indicator: keeps the previous chart visible instead of flashing a skeleton */}
    {loading && hasData && (
      <LinearProgress
        sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: UI.accent } }}
      />
    )}

    <Box sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '10px', color: UI.accent, letterSpacing: '1.2px', textTransform: 'uppercase', fontWeight: 800 }}>
            {eyebrow}
          </Typography>
          <Typography component="h3" sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', mt: 0.25, lineHeight: 1.3 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography component="div" sx={{ fontSize: '12px', color: UI.textSecondary, mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions}
      </Box>

      {controls && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            pb: 2,
            borderBottom: `1px solid ${UI.hairline}`,
          }}
        >
          {controls}
        </Box>
      )}

      {loading && !hasData ? (
        // First load: a placeholder in the chart's shape (legend row, then the plot).
        <Box role="status" aria-label="Loading chart data" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[72, 96, 64, 84].map((w) => (
              <Skeleton key={w} variant="text" width={w} height={16} sx={{ bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
            ))}
          </Box>
          <Skeleton variant="rounded" height={300} sx={{ bgcolor: 'rgba(255, 255, 255, 0.04)', borderRadius: '8px' }} />
        </Box>
      ) : hasData ? (
        <Box sx={{ opacity: loading ? 0.55 : 1, transition: 'opacity 0.2s' }}>{children}</Box>
      ) : error ? (
        <Box
          role="alert"
          sx={{
            py: 5,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(230, 103, 103, 0.05)',
            borderRadius: '8px',
            border: '1px dashed rgba(230, 103, 103, 0.3)',
          }}
        >
          <ErrorIcon sx={{ fontSize: 32, color: '#E66767' }} />
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#BBB' }}>Couldn't load chart data</Typography>
          <Typography sx={{ fontSize: '12px', color: UI.textMuted, maxWidth: 440 }}>{error}</Typography>
          {onRetry && (
            <Button
              size="small"
              variant="outlined"
              onClick={onRetry}
              sx={{ mt: 1, fontSize: '11px', fontWeight: 700, textTransform: 'none', color: UI.text, borderColor: '#333', '&:hover': { borderColor: UI.accent, color: UI.accent } }}
            >
              Retry
            </Button>
          )}
        </Box>
      ) : (
        <Box
          sx={{
            py: 6,
            px: 3,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            bgcolor: 'rgba(0,0,0,0.2)',
            borderRadius: '8px',
            border: '1px dashed #222',
          }}
        >
          {empty.icon}
          <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#BBB' }}>{empty.title}</Typography>
          <Typography sx={{ fontSize: '12px', color: UI.textMuted, maxWidth: 440 }}>{empty.body}</Typography>
          {onUploadClick && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
              onClick={onUploadClick}
              sx={{
                mt: 1,
                fontSize: '11px',
                fontWeight: 700,
                textTransform: 'none',
                color: UI.accent,
                borderColor: 'rgba(16, 185, 129, 0.4)',
                '&:hover': { borderColor: UI.accent, bgcolor: 'rgba(16, 185, 129, 0.1)' },
              }}
            >
              Upload station audio
            </Button>
          )}
        </Box>
      )}

      {hasData && footer}
    </Box>
  </Box>
);

export default ChartCard;
